<template>
  <div class="training-calendar">
    <div class="tc-header">
      <h4 class="tc-title">📅 {{ t('calendar.trainingReservations') }}</h4>

      <div class="tc-toolbar">
        <div class="btn-group">
          <button
            v-for="v in views"
            :key="v"
            class="btn"
            :class="{ active: view === v }"
            @click="setView(v)"
          >
            {{ v.toUpperCase() }}
          </button>
        </div>

        <div class="nav-group">
          <i class="fas fa-chevron-left nav-icon" @click="goPrev" />

          <span class="period-label">{{ currentLabel }}</span>

          <i class="fas fa-chevron-right nav-icon" @click="goNext" />

          <span class="today-chip" @click="goToday">
            {{ t('calendar.buttons.today') }}
          </span>

          <i
            v-if="canCreateReservation"
            class="fas fa-plus action-icon primary-icon"
            :title="t('calendar.buttons.newReservation')"
            @click="openCreateForm"
          />
        </div>
      </div>
    </div>

    <div class="legend">
      <span class="dot dot-pending"></span>
      {{ t('calendar.messages.pending') }}

      <span class="dot dot-confirmed"></span>
      {{ t('calendar.messages.confirmed') }}

      <span class="dot dot-cancelled"></span>
      {{ t('calendar.messages.cancelled') }}
    </div>

    <vue-cal
      class="tc-cal"
      style="height: 620px"
      :hide-view-selector="true"
      :default-view="view"
      :view="view"
      :selected-date="currentDate"
      :events="events"
      :time="true"
      :time-from="8 * 60"
      :time-to="18 * 60"
      :time-step="30"
      :time-cell-height="40"
      :disable-days="[]"
      @cell-click="onDayClick"
      @event-click="onEventClick"
      @view-change="onViewChange"
    />

    <div v-if="showForm" class="reservation-form card">
      <div class="form-head">
        <h5 class="m-0">{{ t('calendar.buttons.newReservation') }}</h5>
        <i class="fas fa-times close-icon" @click="closeForm" />
      </div>

      <form @submit.prevent="submitReservation" class="form-body">
        <label>
          {{ t('calendar.projectType') }}
          <select v-model="form.project_type" required>
            <option value="internal">{{ t('calendar.internal') }}</option>
            <option value="external">{{ t('calendar.external') }}</option>
          </select>
        </label>

        <div class="row-2">
          <label>
            {{ t('calendar.startDate') }}
            <input v-model="form.start_date" type="date" required />
          </label>

          <label>
            {{ t('calendar.endDate') }}
            <input v-model="form.end_date" type="date" required />
          </label>
        </div>

        <div class="row-2">
          <label>
            {{ t('calendar.startTime') }}
            <input v-model="form.start_time" type="time" required />
          </label>

          <label>
            {{ t('calendar.endTime') }}
            <input v-model="form.end_time" type="time" required />
          </label>
        </div>

        <label>
          {{ t('calendar.messages.remarks') }}
          <input
            v-model="form.remarks"
            type="text"
            :placeholder="t('calendar.enterRemarks')"
          />
        </label>

        <label>
          {{ t('calendar.messages.numberOfPersons') }}
          <input
            v-model.number="form.number_of_persons"
            type="number"
            min="1"
            required
          />
        </label>

        <div class="actions">
          <span class="text-action" @click="closeForm">
            {{ t('calendar.buttons.cancel') }}
          </span>

          <span class="text-action primary" @click="submitReservation">
            {{ loading ? '...' : t('calendar.buttons.save') }}
          </span>
        </div>
      </form>
    </div>

    <div v-if="activeEvent" class="event-actions card">
      <div class="form-head">
        <h6 class="m-0">
          {{ t('calendar.messages.reservation') }} {{ activeEvent.id }}
        </h6>

        <i class="fas fa-times close-icon" @click="activeEvent = null" />
      </div>

      <div class="ea-body">
        <p class="ea-line">
          <strong>{{ t('calendar.messages.when') }}</strong>
          {{ formatDateTime(activeEvent.start) }} →
          {{ formatDateTime(activeEvent.end) }}
        </p>

        <p class="ea-line">
          <strong>{{ t('calendar.messages.type') }}:</strong>
          {{ activeEvent.project_type }}
        </p>

        <p class="ea-line">
          <strong>{{ t('calendar.messages.reservationStatus') }}:</strong>
          {{ activeEvent.status }}
        </p>

        <p class="ea-line">
          <strong>{{ t('calendar.messages.remarks') }}</strong>
          {{ activeEvent.remarks || '-' }}
        </p>

        <div class="icon-actions">
          <i
            v-if="canConfirmOrCancel"
            class="fas fa-check action-icon success-icon"
            :class="{ disabled: loading }"
            title="Confirm"
            @click="!loading && setStatus(activeEvent.id, 'confirmed')"
          />

          <i
            v-if="canConfirmOrCancel"
            class="fas fa-times action-icon warning-icon"
            :class="{ disabled: loading }"
            title="Cancel"
            @click="!loading && setStatus(activeEvent.id, 'cancelled')"
          />

          <i
            v-if="canDeleteActiveReservation"
            class="fas fa-trash action-icon danger-icon"
            :class="{ disabled: loading }"
            title="Delete"
            @click="!loading && deleteReservation(activeEvent.id)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import VueCal from 'vue-cal'
import 'vue-cal/dist/vuecal.css'
import { getCurrentUser } from '@/utils/permissions'
import {
  getReservationsByPeriod,
  createReservation,
  updateStatus,
  deleteReservation as apiDeleteReservation
} from '@/services/trainingReservations'

const { t } = useI18n({ useScope: 'global' })

const rawReservations = ref([])
const events = ref([])
const showForm = ref(false)
const activeEvent = ref(null)
const loading = ref(false)

const views = ['year', 'month', 'week', 'day']
const view = ref('week')
const currentDate = ref(new Date())

const currentUser = ref(getCurrentUser?.() || {})

const roleName = computed(() => {
  return currentUser.value?.role || currentUser.value?.roles?.[0]?.name || null
})

const positionCode = computed(() => {
  return (
    currentUser.value?.professionalInformation?.position?.code ||
    currentUser.value?.position_code ||
    null
  )
})

const userId = computed(() => currentUser.value?.id || null)

const isTL = computed(() => positionCode.value === 'TL')
const isMaterialManager = computed(() => roleName.value === 'Material Manager')
const isSuperAdmin = computed(() => roleName.value === 'SuperAdmin')

const canCreateReservation = computed(() => isTL.value)

const canConfirmOrCancel = computed(() => {
  return isMaterialManager.value && activeEvent.value?.status === 'pending'
})

const canDeleteActiveReservation = computed(() => {
  if (!activeEvent.value) return false

  const reservation = activeEvent.value
  const isOwner = reservation.user_id === userId.value

  if (isSuperAdmin.value) {
    return ['pending', 'cancelled'].includes(reservation.status)
  }

  if (isMaterialManagerRole.value) {
    return ['pending', 'cancelled'].includes(reservation.status)
  }

  if (isOwner) {
    return reservation.status === 'pending'
  }

  return false
})

const form = ref(getEmptyForm())

function getEmptyForm(date = '') {
  return {
    start_date: date,
    end_date: date,
    start_time: '08:00',
    end_time: '10:00',
    project_type: 'internal',
    remarks: '',
    number_of_persons: 1
  }
}

const pad2 = (n) => String(n).padStart(2, '0')
const fmtDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
const fmtMonth = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`
const fmtYear = (d) => `${d.getFullYear()}`
const onlyHHmm = (t) => (t || '').slice(0, 5)

function weekRange(d) {
  const day = new Date(d)
  const dow = day.getDay() || 7
  const start = new Date(day)
  start.setDate(day.getDate() - (dow - 1))
  start.setHours(0, 0, 0, 0)

  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  end.setHours(23, 59, 59, 999)

  return { start, end }
}

function getWeekNumber(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const dayNum = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7)
}

const currentLabel = computed(() => {
  const d = currentDate.value
  if (view.value === 'year') return fmtYear(d)
  if (view.value === 'month') return fmtMonth(d)
  if (view.value === 'week') {
    const { start } = weekRange(d)
    return `Week ${getWeekNumber(d)} (${start.toLocaleString('default', { month: 'long' })} ${start.getFullYear()})`
  }
  return fmtDate(d)
})

function normalizeArrayResponse(payload) {
  if (payload?.data && Array.isArray(payload.data)) return payload.data
  if (Array.isArray(payload)) return payload
  if (payload?.items && Array.isArray(payload.items)) return payload.items
  const arrays = Object.values(payload || {}).filter(Array.isArray)
  return arrays[0] || []
}

function uniqueById(list) {
  const map = new Map()
  list.forEach((item) => {
    if (item?.id != null) map.set(item.id, item)
  })
  return [...map.values()]
}

async function fetchReservationsForCurrentView() {
  loading.value = true

  try {
    const d = new Date(currentDate.value)
    let rows = []

    if (view.value === 'day') {
      const res = await getReservationsByPeriod({ date: fmtDate(d) })
      rows = normalizeArrayResponse(res.data)
    } else if (view.value === 'month') {
      const res = await getReservationsByPeriod({ month: fmtMonth(d) })
      rows = normalizeArrayResponse(res.data)
    } else if (view.value === 'year') {
      const res = await getReservationsByPeriod({ year: fmtYear(d) })
      rows = normalizeArrayResponse(res.data)
    } else {
      const { start, end } = weekRange(d)
      const m1 = fmtMonth(start)
      const m2 = fmtMonth(end)

      const r1 = await getReservationsByPeriod({ month: m1 })
      const rows1 = normalizeArrayResponse(r1.data)

      if (m1 === m2) {
        rows = rows1
      } else {
        const r2 = await getReservationsByPeriod({ month: m2 })
        const rows2 = normalizeArrayResponse(r2.data)
        rows = uniqueById([...rows1, ...rows2])
      }
    }

    rawReservations.value = rows
    buildEventsForView()
  } catch (error) {
    console.error('fetchReservationsForCurrentView', error)
    alert(error.response?.data?.message || error.message)
  } finally {
    loading.value = false
  }
}

function toDateTime(dateStr, timeStr, fallback = '00:00') {
  return new Date(`${dateStr}T${onlyHHmm(timeStr) || fallback}`)
}

function inRange(s, e, a, b) {
  return s <= b && e >= a
}

function buildEventsForView() {
  let a
  let b
  const d = new Date(currentDate.value)

  if (view.value === 'year') {
    a = new Date(d.getFullYear(), 0, 1, 0, 0, 0, 0)
    b = new Date(d.getFullYear(), 11, 31, 23, 59, 59, 999)
  } else if (view.value === 'month') {
    a = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0)
    b = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999)
  } else if (view.value === 'week') {
    const range = weekRange(d)
    a = range.start
    b = range.end
  } else {
    a = new Date(d)
    a.setHours(0, 0, 0, 0)
    b = new Date(d)
    b.setHours(23, 59, 59, 999)
  }

  events.value = (rawReservations.value || [])
    .map((r) => {
      const s = toDateTime(r.start_date, r.start_time, '00:00')
      const e = toDateTime(r.end_date, r.end_time, '23:59')

      if (isNaN(s.getTime()) || isNaN(e.getTime())) return null

      const status = r.status || 'pending'
      const booked = status === 'pending' || status === 'confirmed'

      return {
        id: r.id,
        user_id: r.user_id ?? null,
        start: s,
        end: e,
        title: r.remarks || '(sans remarque)',
        content: `${r.project_type || 'N/A'} · ${r.number_of_persons || 0} pers.`,
        class: `${booked ? 'booked' : 'cancelled'} ${status}`,
        status,
        project_type: r.project_type,
        number_of_persons: r.number_of_persons,
        remarks: r.remarks
      }
    })
    .filter(Boolean)
    .filter((ev) => inRange(ev.start, ev.end, a, b))
}

async function setView(v) {
  view.value = v
  await fetchReservationsForCurrentView()
}

async function goToday() {
  currentDate.value = new Date()
  await fetchReservationsForCurrentView()
}

async function goPrev() {
  const d = new Date(currentDate.value)

  if (view.value === 'year') d.setFullYear(d.getFullYear() - 1)
  else if (view.value === 'month') d.setMonth(d.getMonth() - 1)
  else if (view.value === 'week') d.setDate(d.getDate() - 7)
  else d.setDate(d.getDate() - 1)

  currentDate.value = d
  await fetchReservationsForCurrentView()
}

async function goNext() {
  const d = new Date(currentDate.value)

  if (view.value === 'year') d.setFullYear(d.getFullYear() + 1)
  else if (view.value === 'month') d.setMonth(d.getMonth() + 1)
  else if (view.value === 'week') d.setDate(d.getDate() + 7)
  else d.setDate(d.getDate() + 1)

  currentDate.value = d
  await fetchReservationsForCurrentView()
}

function openCreateForm() {
  if (!canCreateReservation.value) return
  showForm.value = true
  form.value = getEmptyForm(fmtDate(currentDate.value))
}

function closeForm() {
  showForm.value = false
  form.value = getEmptyForm()
}

function onDayClick(payload) {
  if (!canCreateReservation.value) return

  const clicked = payload?.date instanceof Date
    ? payload.date
    : payload instanceof Date
      ? payload
      : null

  if (!clicked) return

  const selected = fmtDate(clicked)
  form.value = getEmptyForm(selected)
  showForm.value = true
}

function onEventClick({ event }) {
  activeEvent.value = event
}

async function onViewChange(payload) {
  if (payload?.view && views.includes(payload.view)) {
    view.value = payload.view
  }

  if (payload?.startDate) {
    const d = new Date(payload.startDate)
    if (!isNaN(d.getTime())) currentDate.value = d
  }

  await fetchReservationsForCurrentView()
}

async function submitReservation() {
  if (!canCreateReservation.value) {
    alert('Only Team Leaders can create reservations.')
    return
  }

  try {
    loading.value = true

    const payload = {
      start_date: form.value.start_date,
      end_date: form.value.end_date,
      start_time: onlyHHmm(form.value.start_time),
      end_time: onlyHHmm(form.value.end_time),
      project_type: form.value.project_type,
      remarks: form.value.remarks,
      number_of_persons: form.value.number_of_persons
    }

    await createReservation(payload)
    closeForm()
    await fetchReservationsForCurrentView()
  } catch (error) {
    console.error('submitReservation', error)
    alert(error.response?.data?.message || error.message)
  } finally {
    loading.value = false
  }
}

async function setStatus(id, status) {
  if (!isMaterialManager.value) {
    alert('Only Material Manager can change reservation status.')
    return
  }

  try {
    loading.value = true
    await updateStatus(id, status)
    activeEvent.value = null
    await fetchReservationsForCurrentView()
  } catch (error) {
    console.error('setStatus', error)
    alert(error.response?.data?.message || error.message)
  } finally {
    loading.value = false
  }
}

async function deleteReservation(id) {
  if (!canDeleteActiveReservation.value) {
    alert('You are not authorized to delete this reservation.')
    return
  }

  if (!confirm('Delete this reservation?')) return

  try {
    loading.value = true
    await apiDeleteReservation(id)
    activeEvent.value = null
    await fetchReservationsForCurrentView()
  } catch (error) {
    console.error('deleteReservation', error)
    alert(error.response?.data?.message || error.message)
  } finally {
    loading.value = false
  }
}

function formatDateTime(dt) {
  const d = dt instanceof Date ? dt : new Date(dt)
  if (isNaN(d.getTime())) return '-'

  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

onMounted(fetchReservationsForCurrentView)
</script>

<style scoped>
.training-calendar {
  position: relative;
  padding: 12px;
}

.tc-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.tc-title {
  margin: 0;
  font-weight: 700;
  color: #2b2b2b;
}

.tc-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn {
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #333;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: 0.15s ease;
}

.btn:hover {
  background: #f5f5f5;
}

.btn.active {
  background: #b03a45;
  color: #fff;
  border-color: #b03a45;
}

.btn-group {
  display: flex;
  gap: 8px;
}

.nav-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.period-label {
  font-weight: 600;
  color: #444;
  min-width: 210px;
  text-align: center;
}

.nav-icon,
.action-icon,
.close-icon {
  cursor: pointer;
  font-size: 18px;
}

.primary-icon {
  color: #b03a45;
}

.success-icon {
  color: #16a34a;
}

.warning-icon {
  color: #d97706;
}

.danger-icon {
  color: #dc2626;
}

.close-icon {
  color: #777;
}

.disabled {
  opacity: 0.45;
  pointer-events: none;
}

.today-chip {
  border: 1px solid #d7d7d7;
  border-radius: 20px;
  padding: 6px 12px;
  cursor: pointer;
  color: #666;
}

.legend {
  display: flex;
  gap: 14px;
  align-items: center;
  margin: 8px 0 12px;
  color: #555;
}

.legend .dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
}

.dot-pending {
  background: #f59e0b;
}

.dot-confirmed {
  background: #b03a45;
}

.dot-cancelled {
  background: #ccc;
}

.tc-cal {
  border: 1px solid #eee;
  border-radius: 14px;
  overflow: hidden;
}

.card {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 14px;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.07);
}

.reservation-form {
  position: absolute;
  top: 120px;
  right: 12px;
  width: 360px;
  z-index: 10;
}

.event-actions {
  position: absolute;
  top: 120px;
  left: 12px;
  width: 360px;
  z-index: 10;
}

.form-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
}

.form-body,
.ea-body {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-body .row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.form-body label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: #555;
}

.form-body input,
.form-body select {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
}

.ea-line {
  margin: 0;
  color: #555;
}

.actions,
.icon-actions {
  display: flex;
  gap: 14px;
  justify-content: flex-end;
  align-items: center;
}

.text-action {
  cursor: pointer;
  color: #666;
}

.text-action.primary {
  color: #b03a45;
  font-weight: 600;
}
</style>

<style>
.vuecal__event.booked {
  background: repeating-linear-gradient(
    45deg,
    rgba(176, 58, 69, 0.85),
    rgba(176, 58, 69, 0.85) 8px,
    rgba(176, 58, 69, 0.65) 8px,
    rgba(176, 58, 69, 0.65) 16px
  ) !important;
  color: #fff !important;
  border: none !important;
}

.vuecal__event.cancelled {
  background: #eee !important;
  color: #888 !important;
  border: 1px dashed #ccc !important;
  opacity: 0.7;
}
</style>